import { useEffect, useState } from 'react'
import { Chip, DateRangeField, FilterToolbar, SearchField } from '@shared/design-system/ui'
import { DEFAULT_DATE_RANGE, isDateRangeActive } from '@shared/lib/date-range'
import type { DatePreset, DateRangeValue } from '@shared/lib/date-range'
import { useDebounce } from '@shared/lib/use-debounce'
import {
  multiSelectGroup,
  multiSelectLabel,
  multiSelectOptions,
  optionToggle,
  statementToolbar,
  toolbarControls,
} from './wallet.css'

export interface StatementSearch {
  direction?: 'IN' | 'OUT'
  q?: string
  entry?: string
  preset?: DatePreset
  from?: string
  to?: string
}

const MOVEMENT_OPTIONS = [
  { value: 'OUT', label: 'Débitos' },
  { value: 'IN', label: 'Créditos' },
] as const

function dateRangeFromSearch(search: StatementSearch): DateRangeValue {
  return {
    preset: search.preset ?? DEFAULT_DATE_RANGE.preset,
    from: search.from ?? null,
    to: search.to ?? null,
  }
}

type ToolbarProps = {
  search: StatementSearch
  onChange: (next: StatementSearch) => void
  resultCount: number
}

type DebouncedSearchInputProps = {
  value: string
  onCommit: (value: string | undefined) => void
}

function DebouncedSearchInput({ value, onCommit }: DebouncedSearchInputProps) {
  const [text, setText] = useState(value)
  const debounced = useDebounce(text, 300)

  useEffect(() => {
    const next = debounced.trim()
    if (next === value) {
      return
    }
    onCommit(next.length > 0 ? next : undefined)
  }, [debounced, onCommit, value])

  return (
    <SearchField
      value={text}
      onChange={setText}
      placeholder="Buscar por venda"
      ariaLabel="Buscar no extrato por venda"
    />
  )
}

export function StatementToolbar({ search, onChange, resultCount }: ToolbarProps) {
  const range = dateRangeFromSearch(search)

  function patch(next: Partial<StatementSearch>) {
    onChange({ ...search, ...next })
  }

  function changeDateRange(value: DateRangeValue) {
    patch({
      preset: value.preset,
      from: value.from ?? undefined,
      to: value.to ?? undefined,
    })
  }

  function clearAll() {
    onChange({ entry: search.entry })
  }

  const dateActive = isDateRangeActive(range)
  const hasFilters = Boolean(search.q) || Boolean(search.direction) || dateActive

  const chips = (
    <>
      {search.direction ? (
        <Chip
          label={search.direction === 'OUT' ? 'Débitos' : 'Créditos'}
          onRemove={() => patch({ direction: undefined })}
        />
      ) : null}
    </>
  )

  return (
    <div className={statementToolbar}>
      <FilterToolbar
        chips={hasFilters ? chips : null}
        resultCount={`${resultCount} ${resultCount === 1 ? 'movimentação' : 'movimentações'}`}
        onClear={hasFilters ? clearAll : undefined}
      >
        <div className={toolbarControls}>
          <DebouncedSearchInput
            key={search.q ?? ''}
            value={search.q ?? ''}
            onCommit={(value) => onChange({ ...search, q: value })}
          />
          <DateRangeField value={range} onChange={changeDateRange} />
          <div className={multiSelectGroup}>
            <span className={multiSelectLabel}>Movimentação</span>
            <div className={multiSelectOptions} role="group" aria-label="Filtrar por movimentação">
              {MOVEMENT_OPTIONS.map(({ value, label }) => {
                const active = search.direction === value
                return (
                  <button
                    type="button"
                    key={value}
                    className={optionToggle({ active })}
                    aria-pressed={active}
                    onClick={() => patch({ direction: active ? undefined : value })}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </FilterToolbar>
    </div>
  )
}
